import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

const reviews = [
  {
    name: 'Francesco R.',
    rating: 5,
    text: {
      it: 'Un accessorio che non ho mai visto da nessun\'altra parte. Il cane di profilo in bronzo ossidato, le corna di cervo, i puntali in corna vere — ogni dettaglio è pensato. Lo porto a ogni battuta e tutti me lo chiedono. Artigianato vero.',
      en: 'An accessory I\'ve never seen anywhere else. The oxidized bronze dog profile, the deer antlers, the real horn tips — every detail is thought through. I wear it to every hunt and everyone asks about it. True craftsmanship.',
    },
    product: {
      it: 'Cravattino Cane da Caccia e Corna di Cervo',
      en: 'Hunting Dog & Deer Antler Bow Tie',
    },
  },
  {
    name: 'Luca B.',
    rating: 5,
    text: {
      it: 'Ho regalato questo braccialetto a mio fratello alpinista per il suo compleanno. Quando ha aperto la scatola era senza parole — le piccozze in bronzo sembrano vere, ogni dettaglio è perfetto. Packaging curato, spedizione veloce. Tornerò.',
      en: 'I gifted this bracelet to my mountaineer brother for his birthday. When he opened the box he was speechless — the bronze ice axes look real, every detail is perfect. Beautiful packaging, fast shipping. I\'ll be back.',
    },
    product: {
      it: 'Braccialetto Piccozza',
      en: 'Ice Axe Bracelet',
    },
  },
  {
    name: 'Anna T.',
    rating: 5,
    text: {
      it: 'Tre chiodi in bronzo con finiture oro, rodio e rutenio — ognuno diverso, come quelli veri. La Collana Chiodi è il pezzo che cercavo da anni per raccontare la mia passione per l\'arrampicata classica senza essere banale. Ricevo complimenti ogni volta che la indosso.',
      en: 'Three bronze pitons with gold, rhodium and ruthenium finishes — each one different, like the real ones. The Pitons Necklace is the piece I\'d been looking for years to express my passion for trad climbing without being ordinary. I get compliments every time I wear it.',
    },
    product: {
      it: 'Collana Chiodi',
      en: 'Pitons Necklace',
    },
  },
  {
    name: 'Thomas K.',
    rating: 5,
    text: {
      it: 'Ordinato dalla Germania e non sono rimasto deluso. Due stambecchi che si scontrano, corna ossidate, cuoio fiorentino — questa cintura è esattamente quello che cercavo. Spedizione veloce, packaging bellissimo. Weidmannsheil!',
      en: 'Ordered from Germany and I was not disappointed. Two ibexes clashing, oxidized horns, Florentine leather — this belt is exactly what I was looking for. Shipping was fast, packaging beautiful. Weidmannsheil!',
    },
    product: {
      it: 'Cintura Stambecchi',
      en: 'Ibex Belt',
    },
  },
  {
    name: 'Elena M.',
    rating: 5,
    text: {
      it: 'Arrampico da dodici anni e questo è il regalo più centrato che abbia mai ricevuto. Il rinvio in bronzo con moschettoni oro e rutenio è riconoscibile al primo sguardo. Si sente che dietro c\'è una mano vera — non una macchina.',
      en: 'I\'ve been climbing for twelve years and this is the most spot-on gift I\'ve ever received. The bronze quickdraw with gold and ruthenium carabiners is recognizable at first glance. You can feel there\'s a real hand behind it — not a machine.',
    },
    product: {
      it: 'Braccialetto Rinvio',
      en: 'Quickdraw Bracelet',
    },
  },
  {
    name: 'Maria G.',
    rating: 5,
    text: {
      it: 'Cercavo una cintura che raccontasse la mia passione senza urlarlo. La Cintura Cervo è esattamente questo — cuoio solido, fibbia in bronzo con un dettaglio che chi ama la natura riconosce subito. La porto ogni giorno, in ogni stagione.',
      en: 'I was looking for a belt that would tell my passion without shouting it. The Deer Belt is exactly that — solid leather, bronze buckle with a detail that nature lovers recognize immediately. I wear it every day, in every season.',
    },
    product: {
      it: 'Cintura Cervo',
      en: 'Deer Belt',
    },
  },
  {
    name: 'Stefano R.',
    rating: 5,
    text: {
      it: 'Le Nomic sono le mie piccozze da sempre. Averle al collo in bronzo con tre finiture metalliche è qualcosa che non pensavo fosse possibile trovare. Dettaglio incredibile, qualità altissima. Per chi vive l\'ice climbing è il regalo perfetto.',
      en: 'The Nomics have always been my ice axes. Having them around my neck in bronze with three metallic finishes is something I never thought possible to find. Incredible detail, top quality. For anyone who lives ice climbing, it\'s the perfect gift.',
    },
    product: {
      it: 'Collana Nomic',
      en: 'Nomic Necklace',
    },
  },
  {
    name: 'Marco V.',
    rating: 5,
    text: {
      it: 'Il primo pezzo che ho comprato e non sarà l\'ultimo. Una corda arrotolata in bronzo — chi arrampica capisce subito. L\'ho regalata al mio compagno di cordata dopo la nostra prima via in multipitch. Non se la toglie più dalle chiavi.',
      en: 'The first piece I bought and it won\'t be the last. A coiled rope in bronze — climbers get it immediately. I gifted it to my climbing partner after our first multi-pitch route. He never takes it off his keys.',
    },
    product: {
      it: 'Portachiavi Corda',
      en: 'Rope Keychain',
    },
  },
  {
    name: 'Giorgio P.',
    rating: 5,
    text: {
      it: 'Le corna di capriolo, i rami smaltati, il cordino in pelle — tutto si tiene insieme in modo naturale e autentico. Un pezzo che parla di bosco e tradizione senza essere volgare. Lo regalo spesso agli amici cacciatori, non sbaglio mai.',
      en: 'The roe deer antlers, the enameled branches, the leather cord — everything holds together in a natural and authentic way. A piece that speaks of forest and tradition without being vulgar. I often gift it to hunter friends, never a miss.',
    },
    product: {
      it: 'Collana Corna di Capriolo',
      en: 'Roe Deer Antler Necklace',
    },
  },
];

export function ReviewsSection() {
  const { t, i18n } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', loop: true, slidesToScroll: 1 });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const currentLang = (i18n.language?.startsWith('en') ? 'en' : 'it') as 'it' | 'en';

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const ReviewCard = ({ review, index }: { review: typeof reviews[0]; index: number }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-card rounded-lg p-4 md:p-5 card-hover border border-border h-full"
    >
      <div className="flex items-center gap-0.5 mb-2">
        {Array.from({ length: review.rating }).map((_, i) => (
          <Star key={i} className="w-3 h-3 fill-bronze text-bronze" />
        ))}
      </div>
      <p className="font-heading font-semibold text-foreground text-sm mb-1">
        {review.product[currentLang] || review.product.it}
      </p>
      <blockquote className="font-body text-foreground leading-relaxed mb-3 text-sm">
        "{review.text[currentLang] || review.text.it}"
      </blockquote>
      <span className="font-body text-xs text-muted-foreground">{review.name}</span>
    </motion.div>
  );

  return (
    <section className="py-10 md:py-14 bg-secondary">
      <div className="container mx-auto px-4 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-6"
        >
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-2">
            {t('reviews.title')}
          </h2>
          <p className="font-elegant text-base text-muted-foreground italic">
            {t('reviews.subtitle')}
          </p>
        </motion.div>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4">
              {reviews.map((review, index) => (
                <div key={review.name} className="flex-none basis-[85%] md:basis-[31%]">
                  <ReviewCard review={review} index={index} />
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={scrollPrev}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-card border border-border shadow-sm hover:bg-accent transition-colors disabled:opacity-30"
            disabled={!canScrollPrev}
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4 text-foreground" />
          </button>
          <button
            onClick={scrollNext}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-card border border-border shadow-sm hover:bg-accent transition-colors disabled:opacity-30"
            disabled={!canScrollNext}
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4 text-foreground" />
          </button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-3 md:hidden">
          {t('featured.swipeToSee')}
        </p>
      </div>
    </section>
  );
}
